import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Eye, EyeOff, LogIn, UserPlus, ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'
import { api } from '@/lib/api'
import { Button, Input, Card } from '@/components/common'

type View = 'login' | 'signup'

const LoginPage = () => {
  const navigate = useNavigate()
  const { login, setLoading, setError, isLoading, error } = useAuthStore()

  const [view, setView] = useState<View>('login')

  // Login fields
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Signup fields
  const [suUsername, setSuUsername] = useState('')
  const [suPassword, setSuPassword] = useState('')
  const [suConfirmPassword, setSuConfirmPassword] = useState('')
  const [suEmail, setSuEmail] = useState('')
  const [suPhone, setSuPhone] = useState('')
  const [suSecretKey, setSuSecretKey] = useState('')
  const [showSuPassword, setShowSuPassword] = useState(false)
  const [showSuConfirm, setShowSuConfirm] = useState(false)
  const [signupLoading, setSignupLoading] = useState(false)
  const [signupError, setSignupError] = useState<string | null>(null)

  const resetSignup = () => {
    setSuUsername('')
    setSuPassword('')
    setSuConfirmPassword('')
    setSuEmail('')
    setSuPhone('')
    setSuSecretKey('')
    setSignupError(null)
  }

  const switchView = (v: View) => {
    setError(null)
    setSignupError(null)
    setView(v)
  }

  // ---- Login handler ----
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.auth.login({
        username: username.trim(),
        password,
      })

      if (!response.success || !response.data) {
        const message = response.error ?? 'Invalid username or password'
        setError(message)
        toast.error(message)
        return
      }

      const { token, user } = response.data

      login(
        {
          id: user.id ?? user._id,
          _id: user._id ?? user.id,
          username: user.username,
          convexUserId: user.convexUserId ?? undefined,
          createdAt: user.createdAt ?? Date.now(),
        },
        token,
      )

      if (!user.convexUserId) {
        toast('Signed in, but Convex is unavailable. Project actions may be limited.', {
          icon: 'warning',
        })
      } else {
        toast.success('Welcome to AADYA!')
      }
      navigate('/projects')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed'
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  // ---- Signup handler ----
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setSignupError(null)

    if (!suUsername.trim() || !suPassword || !suConfirmPassword || !suEmail.trim() || !suPhone.trim() || !suSecretKey) {
      setSignupError('All fields are required')
      return
    }

    if (suPassword !== suConfirmPassword) {
      setSignupError('Passwords do not match')
      return
    }

    if (suPassword.length < 6) {
      setSignupError('Password must be at least 6 characters')
      return
    }

    setSignupLoading(true)

    try {
      const response = await api.auth.signup({
        username: suUsername.trim(),
        password: suPassword,
        confirmPassword: suConfirmPassword,
        email: suEmail.trim(),
        phoneNumber: suPhone.trim(),
        secretKey: suSecretKey,
      })

      if (!response.success) {
        const message = response.error ?? 'Signup failed'
        setSignupError(message)
        toast.error(message)
        return
      }

      toast.success('Account created! Please sign in.')
      resetSignup()
      switchView('login')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Signup failed'
      setSignupError(message)
      toast.error(message)
    } finally {
      setSignupLoading(false)
    }
  }

  // ---- Password toggle button ----
  const PasswordToggle = ({ show, toggle }: { show: boolean; toggle: () => void }) => (
    <button
      type="button"
      onClick={toggle}
      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
    >
      {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  )

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12 px-4">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-light-baby-pink/20 dark:bg-dark-burnt-peach/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-light-icy-blue/20 dark:bg-dark-muted-teal/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl overflow-hidden bg-white dark:bg-slate-800 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <img
                src="/aadya_logo.png"
                alt="AADYA logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Welcome to AADYA
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {view === 'login'
                ? 'Sign in to start designing problems'
                : 'Create your account'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {/* ============ LOGIN FORM ============ */}
            {view === 'login' && (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <form onSubmit={handleLogin} className="space-y-5">
                  <Input
                    label="Username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    disabled={isLoading}
                  />

                  <Input
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={isLoading}
                    rightIcon={<PasswordToggle show={showPassword} toggle={() => setShowPassword(!showPassword)} />}
                    error={error || undefined}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    isLoading={isLoading}
                    leftIcon={!isLoading && <LogIn className="w-5 h-5" />}
                  >
                    {isLoading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Don&apos;t have an account?{' '}
                    <button
                      type="button"
                      onClick={() => switchView('signup')}
                      className="font-semibold text-light-mauve dark:text-dark-apricot-cream hover:underline"
                    >
                      Sign Up
                    </button>
                  </p>
                </div>
              </motion.div>
            )}

            {/* ============ SIGNUP FORM ============ */}
            {view === 'signup' && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <form onSubmit={handleSignup} className="space-y-4">
                  <Input
                    label="Username"
                    type="text"
                    value={suUsername}
                    onChange={(e) => setSuUsername(e.target.value)}
                    placeholder="Choose a username"
                    autoComplete="username"
                    disabled={signupLoading}
                  />

                  <Input
                    label="Email"
                    type="email"
                    value={suEmail}
                    onChange={(e) => setSuEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    disabled={signupLoading}
                  />

                  <Input
                    label="Phone Number"
                    type="tel"
                    value={suPhone}
                    onChange={(e) => setSuPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    autoComplete="tel"
                    disabled={signupLoading}
                  />

                  <Input
                    label="Password"
                    type={showSuPassword ? 'text' : 'password'}
                    value={suPassword}
                    onChange={(e) => setSuPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    disabled={signupLoading}
                    rightIcon={<PasswordToggle show={showSuPassword} toggle={() => setShowSuPassword(!showSuPassword)} />}
                  />

                  <Input
                    label="Confirm Password"
                    type={showSuConfirm ? 'text' : 'password'}
                    value={suConfirmPassword}
                    onChange={(e) => setSuConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    disabled={signupLoading}
                    rightIcon={<PasswordToggle show={showSuConfirm} toggle={() => setShowSuConfirm(!showSuConfirm)} />}
                  />

                  <Input
                    label="Secret Key"
                    type="password"
                    value={suSecretKey}
                    onChange={(e) => setSuSecretKey(e.target.value)}
                    placeholder="Enter the invitation key"
                    disabled={signupLoading}
                    error={signupError || undefined}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    isLoading={signupLoading}
                    leftIcon={!signupLoading && <UserPlus className="w-5 h-5" />}
                  >
                    {signupLoading ? 'Creating account...' : 'Sign Up'}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={() => switchView('login')}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-light-mauve dark:text-dark-apricot-cream hover:underline"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Sign In
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </motion.div>
    </div>
  )
}

export default LoginPage
